<template>
  <div class="collapsible-directory">
    <div class="directory-header">
      <h4 class="directory-title">{{ title || 'Project Structure' }}</h4>
      <div class="directory-controls">
        <button @click="expandAll" class="control-btn">Expand All</button>
        <button @click="collapseAll" class="control-btn">Collapse All</button>
      </div>
    </div>
    
    <div class="directory-content">
      <DirectoryTree 
        :content="JSON.stringify(parsedTree)" 
        :title="title"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import DirectoryTree from './DirectoryTree.vue'
import { parseMarkdownDirectory, parseTextDirectory, type DirectoryItem } from '@/utils/directoryParser'

interface Props {
  content?: string
  title?: string
  type?: 'markdown' | 'text' | 'sample'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'markdown',
  title: 'Project Structure'
})

const parsedTree = ref<DirectoryItem[]>([])

// Parse the directory content based on type
const parseContent = () => {
  if (props.content) {
    if (props.type === 'markdown') {
      parsedTree.value = parseMarkdownDirectory(props.content)
    } else {
      parsedTree.value = parseTextDirectory(props.content)
    }
  } else {
    // No content provided, set empty tree
    parsedTree.value = []
  }
}

// Expand all directories
const expandAll = () => {
  const expandRecursive = (items: DirectoryItem[]) => {
    items.forEach(item => {
      if (item.type === 'directory' && item.children) {
        item.isExpanded = true
        expandRecursive(item.children)
      }
    })
  }
  expandRecursive(parsedTree.value)
}

// Collapse all directories
const collapseAll = () => {
  const collapseRecursive = (items: DirectoryItem[]) => {
    items.forEach(item => {
      if (item.type === 'directory' && item.children) {
        item.isExpanded = false
        collapseRecursive(item.children)
      }
    })
  }
  collapseRecursive(parsedTree.value)
  // Trigger reactivity
  parsedTree.value = [...parsedTree.value]
}

onMounted(() => {
  parseContent()
})
</script>

<style scoped>
.collapsible-directory {
  margin: 1.5rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.directory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.directory-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.directory-controls {
  display: flex;
  gap: 0.5rem;
}

.control-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.control-btn:hover {
  color: #374151;
  background: #f9fafb;
  border-color: #9ca3af;
}

.control-btn:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.directory-content {
  padding: 1rem;
  background: white;
}

/* Dark mode support */
:global(.dark) .directory-header {
  background: #1f2937;
  border-bottom-color: #374151;
}

:global(.dark) .directory-title {
  color: #d1d5db;
}

:global(.dark) .control-btn {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

:global(.dark) .control-btn:hover {
  background: #4b5563;
  color: #f9fafb;
}

:global(.dark) .directory-content {
  background: #111827;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .directory-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .directory-controls {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
